import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Primitives";
import { toast } from "sonner";
import { downloadCsv } from "../lib/csv";
import {
    Play, FileDown, Loader2, RefreshCw, Columns3, Check, Clock,
    ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Search, X,
} from "lucide-react";

// -------- formatting helpers --------
const fmtMoney = (v, cur) => `${cur || ""} ${(Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = (v) => (Number(v) || 0).toLocaleString("en-US");
const fmtPct = (v) => `${(Number(v) || 0).toFixed(2)}%`;
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const fmtTimeAgo = (iso) => {
    if (!iso) return "never";
    const t = new Date(iso.endsWith("Z") ? iso : iso + "Z").getTime();
    if (Number.isNaN(t)) return "—";
    let s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 45) return `${s}s ago`;
    if (s < 90) return "1m ago";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ${m % 60}m ago`;
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h ago`;
};

// -------- unified column catalogs per report type --------
// kind: "dim" (dimension) or "metric"; sortKey is the server-side column name
// idField/nameField allows showing both ID + name in the same cell (campaign/IO/LI)
const STANDARD_COLUMNS = [
    { key: "metric_date", label: "Date", kind: "dim", align: "left", sortKey: "metric_date", render: (r) => <span className="font-mono">{r.metric_date}</span> },
    { key: "campaign", label: "Campaign", kind: "dim", align: "left", sortKey: "campaign", nameField: "campaign", idField: "media_plan_id" },
    { key: "insertion_order", label: "Insertion Order", kind: "dim", align: "left", sortKey: "insertion_order", nameField: "insertion_order", idField: "insertion_order_id" },
    { key: "line_item", label: "Line Item", kind: "dim", align: "left", sortKey: "line_item", nameField: "line_item", idField: "line_item_id" },
    { key: "creative", label: "Creative", kind: "dim", align: "left", sortKey: "creative" },
    { key: "creative_size", label: "Creative Size", kind: "dim", align: "left", sortKey: "creative_size" },
    { key: "country", label: "Country", kind: "dim", align: "left", sortKey: "country" },
    { key: "city", label: "City", kind: "dim", align: "left", sortKey: "city" },
    { key: "inventory_source", label: "Inventory Source", kind: "dim", align: "left", sortKey: "inventory_source" },
    { key: "app_url", label: "App / URL", kind: "dim", align: "left", sortKey: "app_url" },
    { key: "impressions", label: "Impressions", kind: "metric", format: "int", sortKey: "impressions" },
    { key: "clicks", label: "Clicks", kind: "metric", format: "int", sortKey: "clicks" },
    { key: "ctr", label: "CTR", kind: "metric", format: "percent", sortKey: "ctr" },
    { key: "spend", label: "Spend", kind: "metric", format: "money", sortKey: "spend" },
    { key: "viewable_impressions", label: "Viewable impr.", kind: "metric", format: "int", sortKey: "viewable_impressions" },
    { key: "video_first_quartile", label: "Video 25%", kind: "metric", format: "int", sortKey: "video_first_quartile" },
    { key: "video_midpoint", label: "Video 50%", kind: "metric", format: "int", sortKey: "video_midpoint" },
    { key: "video_third_quartile", label: "Video 75%", kind: "metric", format: "int", sortKey: "video_third_quartile" },
    { key: "video_completes", label: "Video 100%", kind: "metric", format: "int", sortKey: "video_completes" },
    // Internal-only margin metrics (super_admin + admin with can_view_actual_cost) — server omits the fields for advertisers.
    { key: "client_cpm", label: "Client CPM", kind: "metric", format: "money", sortKey: null, internal: true },
    { key: "display_spend", label: "Campaign Budget", kind: "metric", format: "money", sortKey: null, internal: true },
    { key: "margin", label: "Margin", kind: "metric", format: "money", sortKey: null, internal: true },
    { key: "margin_pct", label: "Margin %", kind: "metric", format: "percent", sortKey: null, internal: true },
];
const REACH_COLUMNS = [
    { key: "metric_date", label: "Date", kind: "dim", align: "left", sortKey: "metric_date", render: (r) => <span className="font-mono">{r.metric_date}</span> },
    { key: "campaign", label: "Campaign", kind: "dim", align: "left", sortKey: "campaign", nameField: "campaign", idField: "media_plan_id" },
    { key: "insertion_order", label: "Insertion Order", kind: "dim", align: "left", sortKey: "insertion_order" },
    { key: "line_item", label: "Line Item", kind: "dim", align: "left", sortKey: "line_item" },
    { key: "creative", label: "Creative", kind: "dim", align: "left", sortKey: "creative" },
    { key: "country", label: "Country", kind: "dim", align: "left", sortKey: "country" },
    { key: "currency", label: "Currency", kind: "dim", align: "left", sortKey: "currency" },
    { key: "impressions", label: "Impressions", kind: "metric", format: "int", sortKey: "impressions" },
    { key: "clicks", label: "Clicks", kind: "metric", format: "int", sortKey: "clicks" },
    { key: "ctr", label: "CTR", kind: "metric", format: "percent", sortKey: "ctr" },
    { key: "spend", label: "Spend", kind: "metric", format: "money", sortKey: "spend" },
    { key: "viewable_impressions", label: "Viewable impr.", kind: "metric", format: "int", sortKey: "viewable_impressions" },
    { key: "unique_reach_total", label: "Unique reach", kind: "metric", format: "int", sortKey: "unique_reach_total" },
    { key: "video_first_quartile", label: "Video 25%", kind: "metric", format: "int", sortKey: "video_first_quartile" },
    { key: "video_midpoint", label: "Video 50%", kind: "metric", format: "int", sortKey: "video_midpoint" },
    { key: "video_third_quartile", label: "Video 75%", kind: "metric", format: "int", sortKey: "video_third_quartile" },
    { key: "video_completes", label: "Video 100%", kind: "metric", format: "int", sortKey: "video_completes" },
    // Internal-only margin metrics
    { key: "client_cpm", label: "Client CPM", kind: "metric", format: "money", sortKey: null, internal: true },
    { key: "display_spend", label: "Campaign Budget", kind: "metric", format: "money", sortKey: null, internal: true },
    { key: "margin", label: "Margin", kind: "metric", format: "money", sortKey: null, internal: true },
    { key: "margin_pct", label: "Margin %", kind: "metric", format: "percent", sortKey: null, internal: true },
];

const LS_KEYS = {
    STANDARD: "dv360_columns_standard_v2",
    REACH: "dv360_columns_reach_v2",
    type: "dv360_report_type_v1",
};
const DEFAULTS = {
    STANDARD: ["metric_date", "campaign", "insertion_order", "line_item", "country", "city", "impressions", "clicks", "ctr", "spend", "viewable_impressions"],
    REACH: ["metric_date", "campaign", "insertion_order", "line_item", "country", "impressions", "clicks", "ctr", "spend", "unique_reach_total"],
};

export default function DV360DetailedReports() {
    const { user } = useAuth();
    const [accounts, setAccounts] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [ad_account_id, setAA] = useState("");
    const [campaign_id, setCamp] = useState("");
    const [date_from, setFrom] = useState(daysAgo(7));
    const [date_to, setTo] = useState(today());
    const [rows, setRows] = useState([]);
    const [serverTotals, setServerTotals] = useState(null);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [sortBy, setSortBy] = useState("metric_date");
    const [sortDir, setSortDir] = useState("desc");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [generating, setGenerating] = useState(false);
    const [hasGenerated, setHasGenerated] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

    // Report type
    const [reportType, setReportType] = useState(() => {
        const stored = (localStorage.getItem(LS_KEYS.type) || "").toUpperCase();
        return stored === "REACH" ? "REACH" : "STANDARD";
    });
    useEffect(() => { try { localStorage.setItem(LS_KEYS.type, reportType); } catch (e) { void e; } }, [reportType]);

    const allColumnsRaw = reportType === "STANDARD" ? STANDARD_COLUMNS : REACH_COLUMNS;
    const isInternalUser = user && (user.role === "super_admin" || user.role === "admin");

    // Margin metrics (`client_cpm`, `display_spend`, `margin`, `margin_pct`) are internal-only.
    // The server omits these fields for advertisers; we also hide the picker entries so
    // advertisers never see the columns at all.
    const allColumns = useMemo(
        () => allColumnsRaw.filter((c) => !(c.internal && !isInternalUser)),
        [allColumnsRaw, isInternalUser]
    );

    // Column visibility (persisted per report type)
    const [visibleKeys, setVisibleKeys] = useState(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(LS_KEYS[reportType]) || "null");
            if (Array.isArray(stored) && stored.length) return stored;
        } catch (e) { void e; }
        return DEFAULTS[reportType];
    });
    useEffect(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(LS_KEYS[reportType]) || "null");
            setVisibleKeys(Array.isArray(stored) && stored.length ? stored : DEFAULTS[reportType]);
        } catch (e) { void e; setVisibleKeys(DEFAULTS[reportType]); }
        setRows([]); setServerTotals(null); setTotalRows(0); setPage(1); setHasGenerated(false);
        setSortBy("metric_date"); setSortDir("desc");
    }, [reportType]);
    useEffect(() => { try { localStorage.setItem(LS_KEYS[reportType], JSON.stringify(visibleKeys)); } catch (e) { void e; } }, [visibleKeys, reportType]);

    const [colMenuOpen, setColMenuOpen] = useState(false);
    const toggleKey = (k) => setVisibleKeys((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
    const selectAll = () => setVisibleKeys(allColumns.map((c) => c.key));
    const clearAll = () => setVisibleKeys([]);
    // Render columns in catalog order for consistent layout regardless of toggle order
    const visibleColumns = allColumns.filter((c) => visibleKeys.includes(c.key));

    // ----- sync status badges -----
    const [syncStatus, setSyncStatus] = useState([]);
    const loadSyncStatus = useCallback(async () => {
        try {
            const r = await api.get("/dv360/connections/last-synced");
            setSyncStatus(r.data || []);
        } catch (e) { void e; }
    }, []);
    useEffect(() => {
        loadSyncStatus();
        const id = setInterval(loadSyncStatus, 30 * 1000);
        return () => clearInterval(id);
    }, [loadSyncStatus]);
    const [, forceTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => forceTick((v) => v + 1), 60 * 1000);
        return () => clearInterval(id);
    }, []);

    // ----- data loaders -----
    useEffect(() => {
        api.get("/ad-accounts").then((r) => setAccounts(r.data.filter((a) => a.platform === "dv360")));
    }, []);
    useEffect(() => {
        if (!ad_account_id) { setCampaigns([]); return; }
        api.get(`/campaigns?ad_account_id=${ad_account_id}&platform=dv360&has_metrics=false`).then((r) => setCampaigns(r.data));
        setRows([]); setServerTotals(null); setTotalRows(0); setPage(1); setHasGenerated(false);
    }, [ad_account_id]);

    // Debounce search input → debouncedSearch
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(id);
    }, [search]);

    // ----- fetch -----
    const fetchPage = useCallback(async (targetPage, targetSize, targetSortBy, targetSortDir, targetSearch) => {
        const qs = new URLSearchParams({
            date_from,
            date_to,
            report_type: reportType,
            page: String(targetPage),
            page_size: String(targetSize),
            sort_by: targetSortBy,
            sort_dir: targetSortDir,
        });
        if (ad_account_id) qs.set("ad_account_id", ad_account_id);
        if (targetSearch) qs.set("search", targetSearch);
        // Auto group_by = visible dim columns (in catalog order, for stable URLs/server cache)
        const dimsVisible = allColumns
            .filter((c) => c.kind === "dim" && visibleKeys.includes(c.key))
            .map((c) => c.key);
        if (dimsVisible.length > 0) qs.set("group_by", dimsVisible.join(","));
        const { data } = await api.get(`/dv360/insights?${qs.toString()}`);
        return data;
    }, [date_from, date_to, reportType, ad_account_id, allColumns, visibleKeys]);

    const applyResponse = (data) => {
        const camp = campaigns.find((c) => c.id === campaign_id);
        const filtered = camp
            ? (data.rows || []).filter((r) => (r.media_plan_id === camp.external_campaign_id) || (r.campaign || "").includes(camp.name || "___"))
            : (data.rows || []);
        setRows(filtered);
        setServerTotals(data.totals || null);
        setTotalRows(data.total || 0);
        setHasGenerated(true);
    };

    const generate = async () => {
        setGenerating(true);
        try {
            const data = await fetchPage(1, pageSize, sortBy, sortDir, debouncedSearch);
            setPage(1);
            applyResponse(data);
            toast.success(`${data.total.toLocaleString("en-US")} ${reportType.toLowerCase()} rows in filter · showing page 1`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Generate failed");
        } finally { setGenerating(false); }
    };

    // Auto re-fetch when search / sort / visible-dimension set changes (after first generate)
    const dimSignature = useMemo(
        () => allColumns.filter((c) => c.kind === "dim" && visibleKeys.includes(c.key)).map((c) => c.key).join(","),
        [allColumns, visibleKeys]
    );
    const firstSearchRef = useRef(true);
    useEffect(() => {
        if (!hasGenerated) { firstSearchRef.current = true; return; }
        if (firstSearchRef.current) { firstSearchRef.current = false; return; }
        // search / sort / dim-set changed → reset page to 1
        (async () => {
            setGenerating(true);
            try {
                const data = await fetchPage(1, pageSize, sortBy, sortDir, debouncedSearch);
                setPage(1);
                applyResponse(data);
            } catch (e) {
                toast.error(e.response?.data?.detail || "Reload failed");
            } finally { setGenerating(false); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, sortBy, sortDir, dimSignature]);

    const gotoPage = async (target) => {
        const clamped = Math.min(Math.max(1, target), totalPages);
        if (clamped === page) return;
        setGenerating(true);
        try {
            const data = await fetchPage(clamped, pageSize, sortBy, sortDir, debouncedSearch);
            setPage(clamped);
            applyResponse(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Page fetch failed");
        } finally { setGenerating(false); }
    };

    const changePageSize = async (newSize) => {
        setPageSize(newSize);
        if (hasGenerated) {
            setGenerating(true);
            try {
                const data = await fetchPage(1, newSize, sortBy, sortDir, debouncedSearch);
                setPage(1);
                applyResponse(data);
            } catch (e) {
                toast.error(e.response?.data?.detail || "Page fetch failed");
            } finally { setGenerating(false); }
        }
    };

    const onHeaderClick = (col) => {
        if (!col.sortKey) return;
        if (sortBy === col.sortKey) {
            setSortDir((d) => (d === "desc" ? "asc" : "desc"));
        } else {
            setSortBy(col.sortKey);
            setSortDir("desc");
        }
    };

    const refreshTodayYesterday = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.post("/dv360/refresh-today-yesterday");
            toast.success(data.message || "Refresh queued");
            setTimeout(loadSyncStatus, 4000);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Refresh failed");
        } finally { setRefreshing(false); }
    };

    const [backfilling, setBackfilling] = useState(false);
    // One-click backfill of every DV360 advertiser under every connection in scope from
    // 1 Jun 2026 → today. Bid Manager reports for a 60+ day window take several minutes each,
    // so the backend spawns a daemon thread per connection and this button just fires + confirms.
    const backfillFromJune1 = async () => {
        if (!window.confirm("Backfill every campaign under every DV360 connection from 1 Jun 2026 → today?\n\nBid Manager reports for a wide window take several minutes per connection to finish. Progress will show up on the connection cards; refresh the page in ~5-10 min to see the new rows.")) return;
        setBackfilling(true);
        try {
            const { data } = await api.post("/dv360/backfill", null, { params: { since: "2026-06-01" } });
            toast.success(data.message || "Backfill queued");
            setTimeout(loadSyncStatus, 4000);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Backfill failed");
        } finally { setBackfilling(false); }
    };

    // ----- derived: totals + render helpers -----
    const totals = useMemo(() => {
        if (serverTotals) return { ...serverTotals, _count: totalRows, _currency: serverTotals.currency || null };
        return { _count: rows.length };
    }, [rows.length, serverTotals, totalRows]);

    const renderCell = (col, row) => {
        if (col.render) return col.render(row);
        if (col.kind === "metric") {
            const v = row[col.key];
            if (col.format === "money") return fmtMoney(v, row.currency);
            if (col.format === "percent") return fmtPct(v);
            return fmtN(v);
        }
        // Dimension cell — if nameField + idField, show stacked "Name / ID"
        if (col.nameField && col.idField) {
            const name = row[col.nameField];
            const id = row[col.idField];
            return (
                <div className="min-w-0">
                    <div className="truncate max-w-[280px]" title={name}>{name || <span className="text-zinc-300">—</span>}</div>
                    {id && <div className="text-[10px] text-zinc-400 font-mono truncate">{id}</div>}
                </div>
            );
        }
        const v = row[col.key];
        if (v === null || v === undefined || v === "") return <span className="text-zinc-300">—</span>;
        return <span className="truncate max-w-[200px] inline-block align-middle" title={String(v)}>{String(v)}</span>;
    };

    const renderTotalCell = (col) => {
        if (col.kind !== "metric") return null;
        if (col.format === "percent") return fmtPct(totals[col.key]);
        if (col.format === "money") {
            return totals._currency
                ? fmtMoney(totals[col.key], totals._currency)
                : (totals[col.key] || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
        return fmtN(totals[col.key]);
    };

    const downloadVisible = async () => {
        if (!hasGenerated) return;
        const exportCols = visibleColumns.map((c) => {
            // Expand campaign/IO/LI into name + id pairs for CSV clarity
            if (c.nameField && c.idField) {
                return [
                    { key: c.nameField, label: `${c.label} Name` },
                    { key: c.idField, label: `${c.label} ID` },
                ];
            }
            return [{ key: c.key, label: c.label }];
        }).flat();
        setDownloading(true);
        try {
            const data = await fetchPage(1, 10000, sortBy, sortDir, debouncedSearch);
            let allRows = data.rows || [];
            let p = 1;
            while ((p * 10000) < (data.total || 0)) {
                p += 1;
                const next = await fetchPage(p, 10000, sortBy, sortDir, debouncedSearch);
                allRows = allRows.concat(next.rows || []);
            }
            const camp = campaigns.find((c) => c.id === campaign_id);
            const exportRows = camp
                ? allRows.filter((r) => (r.media_plan_id === camp.external_campaign_id) || (r.campaign || "").includes(camp.name || "___"))
                : allRows;
            downloadCsv(`dv360_${reportType.toLowerCase()}_${ad_account_id || "all"}_${date_from}_${date_to}.csv`, exportCols, exportRows);
            toast.success(`Downloaded ${exportRows.length.toLocaleString("en-US")} rows · ${visibleColumns.length} columns`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Download failed");
        } finally { setDownloading(false); }
    };

    // ----- group columns in picker for clarity -----
    const dimCols = allColumns.filter((c) => c.kind === "dim");
    const metricCols = allColumns.filter((c) => c.kind === "metric");

    return (
        <div data-testid="dv360-detailed-reports-page">
            <PageHeader
                eyebrow="DV360 · Detailed reports"
                title="DV360 Detailed Insights"
                subtitle="Pick a report type, date range, and click Generate. Background sync runs every 1 hour at the PARTNER level. Click any column header to sort, use the search box to find a campaign/creative/geo."
                actions={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={refreshTodayYesterday}
                            disabled={refreshing || backfilling}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50"
                            data-testid="dv-refresh-today-btn"
                        >
                            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            {refreshing ? "Queueing…" : "Refresh today+yesterday"}
                        </button>
                        <button
                            onClick={backfillFromJune1}
                            disabled={refreshing || backfilling}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50"
                            data-testid="dv-backfill-jun1-btn"
                            title="Pull DV360 Bid Manager reports for every advertiser under every partner from 1 Jun 2026 → today"
                        >
                            {backfilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            {backfilling ? "Queueing…" : "Backfill from Jun 1"}
                        </button>
                    </div>
                }
            />

            {/* Sync badges */}
            {syncStatus.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="dv-sync-badges">
                    {syncStatus.map((s) => {
                        const bm = s.bm_queries || {};
                        const lastStatus = bm.STANDARD?.last_run_status || bm.REACH?.last_run_status;
                        const dot = lastStatus === "success" ? "bg-emerald-500" : lastStatus === "failed" ? "bg-red-500" : "bg-zinc-300";
                        return (
                            <div key={s.connection_id} className="inline-flex items-center gap-2 bg-white border border-zinc-200 rounded-md px-3 py-1.5 text-xs" data-testid={`dv-sync-badge-${s.connection_id}`}>
                                <span className={`h-2 w-2 rounded-full ${dot}`} />
                                <span className="font-medium text-zinc-900">{s.display_name}</span>
                                <span className="text-zinc-400">·</span>
                                <span className="text-zinc-500">Partner {s.partner_id}</span>
                                <span className="text-zinc-400">·</span>
                                <Clock className="h-3 w-3 text-zinc-400" />
                                <span className="text-zinc-700 tabular-nums">{fmtTimeAgo(s.last_synced_at)}</span>
                                {bm.STANDARD?.last_row_count > 0 && <span className="text-zinc-400">· STD {bm.STANDARD.last_row_count.toLocaleString("en-US")}</span>}
                                {bm.REACH?.last_row_count > 0 && <span className="text-zinc-400">· REACH {bm.REACH.last_row_count.toLocaleString("en-US")}</span>}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-zinc-200 rounded p-4 mb-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div>
                        <label className="overline">Report type</label>
                        <select value={reportType} onChange={(e) => setReportType(e.target.value)} className="w-full mt-1 px-2 py-2 bg-zinc-100 border border-zinc-200 rounded font-medium" data-testid="dv-report-type-select">
                            <option value="STANDARD">Standard</option>
                            <option value="REACH">Reach</option>
                        </select>
                    </div>
                    <div>
                        <label className="overline">Advertiser <span className="text-zinc-400">(filter)</span></label>
                        <select value={ad_account_id} onChange={(e) => { setAA(e.target.value); setCamp(""); }} className="w-full mt-1 px-2 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="dv-aa-select">
                            <option value="">All advertisers</option>
                            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="overline">Campaign</label>
                        <select value={campaign_id} onChange={(e) => setCamp(e.target.value)} disabled={!ad_account_id} className="w-full mt-1 px-2 py-2 bg-zinc-100 border border-zinc-200 rounded disabled:opacity-50" data-testid="dv-camp-select">
                            <option value="">All campaigns</option>
                            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="overline">From</label>
                        <input type="date" value={date_from} onChange={(e) => setFrom(e.target.value)} className="w-full mt-1 px-2 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="dv-from" />
                    </div>
                    <div>
                        <label className="overline">To</label>
                        <input type="date" value={date_to} onChange={(e) => setTo(e.target.value)} className="w-full mt-1 px-2 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="dv-to" />
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-zinc-100">
                    {/* Search */}
                    <div className="relative">
                        <Search className="h-4 w-4 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search campaign / IO / LI / creative / geo…"
                            className="pl-8 pr-8 py-2 w-80 bg-zinc-100 border border-zinc-200 rounded text-sm"
                            data-testid="dv-search-input"
                        />
                        {search && (
                            <button
                                onClick={() => setSearch("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                                data-testid="dv-search-clear"
                                aria-label="Clear search"
                            ><X className="h-4 w-4" /></button>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Column visibility picker */}
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setColMenuOpen((v) => !v)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium border border-zinc-300 hover:bg-zinc-100 text-zinc-700"
                                data-testid="dv-metrics-btn"
                            >
                                <Columns3 className="h-4 w-4" />
                                Columns ({visibleKeys.length}/{allColumns.length})
                            </button>
                            {colMenuOpen && (
                                <>
                                    <div className="fixed inset-0 z-30" onClick={() => setColMenuOpen(false)} />
                                    <div className="absolute right-0 mt-1 w-80 bg-white border border-zinc-200 rounded-md shadow-lg z-40" data-testid="dv-metrics-menu">
                                        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
                                            <span className="overline">{reportType} columns</span>
                                            <div className="flex items-center gap-2 text-[11px]">
                                                <button type="button" onClick={selectAll} className="wl-accent-text hover:underline" data-testid="dv-metrics-all">Select all</button>
                                                <span className="text-zinc-300">·</span>
                                                <button type="button" onClick={clearAll} className="text-zinc-500 hover:text-zinc-900" data-testid="dv-metrics-clear">Clear</button>
                                            </div>
                                        </div>
                                        <div className="max-h-[28rem] overflow-y-auto py-1">
                                            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-zinc-400">Dimensions</div>
                                            {dimCols.map((c) => {
                                                const checked = visibleKeys.includes(c.key);
                                                return (
                                                    <label key={c.key} className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-50 cursor-pointer text-sm" data-testid={`dv-col-${c.key}`}>
                                                        <span className={`h-4 w-4 rounded border flex items-center justify-center ${checked ? "wl-accent-bg wl-accent-border" : "border-zinc-300 bg-white"}`}>
                                                            {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                                        </span>
                                                        <input type="checkbox" checked={checked} onChange={() => toggleKey(c.key)} className="sr-only" data-testid={`dv-metric-toggle-${c.key}`} />
                                                        <span className="text-zinc-700">{c.label}</span>
                                                    </label>
                                                );
                                            })}
                                            <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-zinc-400">Metrics</div>
                                            {metricCols.map((c) => {
                                                const checked = visibleKeys.includes(c.key);
                                                return (
                                                    <label key={c.key} className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-50 cursor-pointer text-sm" data-testid={`dv-col-${c.key}`}>
                                                        <span className={`h-4 w-4 rounded border flex items-center justify-center ${checked ? "wl-accent-bg wl-accent-border" : "border-zinc-300 bg-white"}`}>
                                                            {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                                        </span>
                                                        <input type="checkbox" checked={checked} onChange={() => toggleKey(c.key)} className="sr-only" data-testid={`dv-metric-toggle-${c.key}`} />
                                                        <span className="text-zinc-700">{c.label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <button
                            onClick={downloadVisible}
                            disabled={!hasGenerated || downloading}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-700"
                            data-testid="dv-download-csv"
                            title={hasGenerated ? `Download all ${totalRows.toLocaleString("en-US")} rows in this filter` : "Generate first"}
                        >
                            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                            {downloading ? "Exporting…" : "Download CSV"}
                        </button>
                        <button
                            onClick={generate}
                            disabled={generating}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                            style={{ background: "var(--wl-primary-hex)", color: "#fff" }}
                            data-testid="dv-generate-btn"
                        >
                            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                            {generating ? "Loading…" : "Generate"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Status line */}
            {hasGenerated && (
                <div className="text-xs text-zinc-500 mb-2 px-1 tabular-nums">
                    {totalRows.toLocaleString("en-US")} {reportType.toLowerCase()} rows
                    {totalRows > rows.length && <span className="text-zinc-400"> · page {page} of {totalPages.toLocaleString("en-US")}</span>}
                    {debouncedSearch && <span className="text-zinc-400"> · search “{debouncedSearch}”</span>}
                    {sortBy && <span className="text-zinc-400"> · sorted by {sortBy} {sortDir}</span>}
                </div>
            )}

            {/* Table */}
            <div className="bg-white border border-zinc-200 overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="bg-zinc-100 border-b border-zinc-200">
                            {visibleColumns.map((col) => {
                                const isActive = col.sortKey && sortBy === col.sortKey;
                                const Arrow = isActive ? (sortDir === "desc" ? ChevronDown : ChevronUp) : null;
                                const align = col.kind === "metric" ? "text-right" : "text-left";
                                return (
                                    <th key={col.key} className={`overline ${align} p-3 whitespace-nowrap select-none ${col.sortKey ? "cursor-pointer hover:bg-zinc-200/60" : ""}`} onClick={() => onHeaderClick(col)} data-testid={`dv-th-${col.key}`}>
                                        <span className={`inline-flex items-center gap-1 ${col.kind === "metric" ? "justify-end w-full" : ""}`}>
                                            {col.label}
                                            {Arrow && <Arrow className="h-3 w-3 wl-accent-text" />}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={visibleColumns.length || 1} className="p-10 text-center text-zinc-500">
                                {generating
                                    ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading {reportType.toLowerCase()} data…</span>
                                    : hasGenerated
                                        ? `No ${reportType.toLowerCase()} rows for this filter. Try widening the date range or clearing the search.`
                                        : `Pick filters → set dates → Generate to load ${reportType.toLowerCase()} data.`}
                            </td></tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                                {visibleColumns.map((col) => {
                                    const align = col.kind === "metric" ? "text-right tabular-nums" : "";
                                    return (
                                        <td key={col.key} className={`p-3 ${align}`}>{renderCell(col, r)}</td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                    {rows.length > 0 && visibleColumns.length > 0 && (
                        <tfoot>
                            <tr className="bg-zinc-100 border-t-2 border-zinc-300 font-semibold" data-testid="dv-totals">
                                {visibleColumns.map((col, idx) => {
                                    const isMetric = col.kind === "metric";
                                    if (idx === 0 && !isMetric) {
                                        return <td key={col.key} className="p-3 text-zinc-900 overline">Filter total ({totals._count.toLocaleString("en-US")} rows)</td>;
                                    }
                                    return (
                                        <td key={col.key} className={`p-3 ${isMetric ? "text-right tabular-nums text-zinc-900" : ""}`} data-testid={isMetric ? `dv-total-${col.key}` : undefined}>
                                            {isMetric ? renderTotalCell(col) : ""}
                                        </td>
                                    );
                                })}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {/* Pagination footer */}
            {hasGenerated && totalRows > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 mt-3 px-1 text-sm" data-testid="dv-pagination">
                    <div className="flex items-center gap-3 text-zinc-600">
                        <span className="tabular-nums">
                            Showing <span className="font-medium text-zinc-900">{((page - 1) * pageSize + 1).toLocaleString("en-US")}</span>
                            –<span className="font-medium text-zinc-900">{Math.min(page * pageSize, totalRows).toLocaleString("en-US")}</span>
                            <span className="text-zinc-400"> of </span>
                            <span className="font-medium text-zinc-900">{totalRows.toLocaleString("en-US")}</span>
                        </span>
                        <span className="text-zinc-300">·</span>
                        <label className="inline-flex items-center gap-2 text-zinc-500">
                            <span className="overline">Rows per page</span>
                            <select
                                value={pageSize}
                                onChange={(e) => changePageSize(parseInt(e.target.value, 10))}
                                className="px-2 py-1 bg-white border border-zinc-200 rounded text-sm"
                                data-testid="dv-page-size-select"
                            >
                                {[25, 50, 100, 250].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => gotoPage(1)} disabled={page <= 1 || generating} className="px-2 py-1.5 rounded text-xs font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="dv-page-first">First</button>
                        <button onClick={() => gotoPage(page - 1)} disabled={page <= 1 || generating} className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-xs font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="dv-page-prev"><ChevronLeft className="h-3.5 w-3.5" />Prev</button>
                        <span className="px-3 py-1.5 text-xs font-medium tabular-nums text-zinc-700" data-testid="dv-page-indicator">
                            Page {page.toLocaleString("en-US")} <span className="text-zinc-400">of</span> {totalPages.toLocaleString("en-US")}
                        </span>
                        <button onClick={() => gotoPage(page + 1)} disabled={page >= totalPages || generating} className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-xs font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="dv-page-next">Next<ChevronRight className="h-3.5 w-3.5" /></button>
                        <button onClick={() => gotoPage(totalPages)} disabled={page >= totalPages || generating} className="px-2 py-1.5 rounded text-xs font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="dv-page-last">Last</button>
                    </div>
                </div>
            )}
        </div>
    );
}
